import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, UserPlus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PageHeader, Panel, EmptyState, ErrorBanner, StatusPill } from "../../components/app/ui";
import AddPatientModal from "./AddPatientModal";
import PatientDrawer from "./PatientDrawer";

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(searchParams.get("add") === "1");
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const load = useCallback(async (term = "") => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/patients", { params: { search: term } });
      setPatients(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(value), 350);
  };

  const closeModal = () => {
    setModalOpen(false);
    if (searchParams.get("add")) setSearchParams({}, { replace: true });
  };

  const onCreated = (startedReview) => {
    closeModal();
    if (startedReview) {
      toast.success("Patient added — AI review started.");
      navigate("/reviews");
      return;
    }
    toast.success("Patient added to your clinic.");
    load(search);
  };

  return (
    <div data-testid="patients-page">
      <PageHeader testId="patients-header" title="Patients" sub="Every patient in your clinic, live from the database. Click a row to open the full record.">
        <button
          type="button"
          data-testid="add-patient-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)] active:scale-[0.98]"
        >
          <UserPlus className="w-4 h-4" strokeWidth={2} />
          Add Patient
        </button>
      </PageHeader>
      <ErrorBanner message={error} onRetry={() => load(search)} />

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.7} />
        <input
          data-testid="patient-search-input"
          type="text"
          value={search}
          onChange={onSearchChange}
          placeholder="Search by name or patient ID…"
          className="w-full bg-white border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
        />
      </div>

      <Panel testId="patients-table" className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[2fr_1fr_1.2fr_1.4fr_0.8fr_24px] gap-4 px-6 py-4 border-b border-slate-100 text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400">
          <span>Patient</span>
          <span>ID</span>
          <span>Treatment</span>
          <span>Stage</span>
          <span>Status</span>
          <span />
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : patients.length === 0 ? (
          <EmptyState
            testId="patients-empty"
            title={search ? "No patients match your search" : "No patients yet"}
            sub={search ? "Try a different name or patient ID." : "Add your first patient to get started."}
          />
        ) : (
          patients.map((p) => {
            const pct = p.treatment_total_stages
              ? Math.round((p.treatment_stage / p.treatment_total_stages) * 100)
              : 0;
            return (
              <div
                key={p.patient_id}
                data-testid={`patient-row-${p.display_id}`}
                onClick={() => setSelectedId(p.patient_id)}
                className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1.2fr_1.4fr_0.8fr_24px] gap-4 items-center px-6 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80 transition-colors duration-200 cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB]/80 to-[#6366F1]/80 flex items-center justify-center text-xs font-bold text-white shrink-0">
                    {p.first_name[0]}{p.last_name[0]}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{p.first_name} {p.last_name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{p.email || p.phone || "—"}</p>
                  </div>
                </div>
                <span className="text-sm text-[#1D4ED8] font-medium">{p.display_id}</span>
                <span className="text-sm text-slate-600">{p.treatment_type}</span>
                <div>
                  <div className="flex justify-between text-[11px] text-slate-400 mb-1.5">
                    <span>Stage {p.treatment_stage} of {p.treatment_total_stages}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] transition-[width] duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <StatusPill status={p.status} />
                <ChevronRight className="w-4 h-4 text-slate-300 hidden md:block" strokeWidth={1.8} />
              </div>
            );
          })
        )}
      </Panel>

      <AddPatientModal open={modalOpen} onClose={closeModal} onCreated={onCreated} />
      <PatientDrawer patientId={selectedId} onClose={() => setSelectedId(null)} onChanged={() => load(search)} />
    </div>
  );
}
